/**
 * ProfileHeader - Compact profile information display
 * Based on mobile app ProfileScreenOptimized.tsx
 */

import { Check } from 'lucide-react'
import Avatar from '../ui/Avatar'

interface ProfileHeaderProps {
  user: {
    name: string
    username: string
    avatar?: string
    bio?: string
    verified?: boolean
    stats: {
      followers: number
      following: number
      posts?: number
    }
  }
  onAvatarClick?: () => void
}

export default function ProfileHeader({ user, onAvatarClick }: ProfileHeaderProps) {
  return (
    <div className="px-4 py-6 border-b border-gray-200">
      {/* Compact Horizontal Layout */}
      <div className="flex gap-4 items-start">
        {/* Avatar */}
        <Avatar
          src={user.avatar}
          alt={user.name}
          size="xl"
          onClick={onAvatarClick}
          className="cursor-pointer"
        />

        {/* User Info & Stats */}
        <div className="flex-1 min-w-0">
          {/* Name with Verification Badge */}
          <div className="flex items-center gap-2">
            <h1 className="text-title truncate">{user.name}</h1>
            {user.verified && (
              <div className="flex-shrink-0 w-5 h-5 bg-onrecord-orange rounded-full flex items-center justify-center">
                <Check className="w-3 h-3 text-white" strokeWidth={3} />
              </div>
            )}
          </div>

          {/* Username */}
          <div className="text-handle">@{user.username}</div>

          {/* Bio - Truncated to 1 line */}
          {user.bio && (
            <p className="text-post text-gray-900 mt-2 truncate">
              {user.bio}
            </p>
          )}

          {/* Stats - Followers & Following */}
          <div className="flex gap-4 mt-3">
            <div className="flex flex-col">
              <span className="text-title">{user.stats.followers.toLocaleString()}</span>
              <span className="text-small text-text-secondary">Followers</span>
            </div>
            <div className="flex flex-col">
              <span className="text-title">{user.stats.following.toLocaleString()}</span>
              <span className="text-small text-text-secondary">Following</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
